import { combineLatestDeepObject } from '@/utils/rxjs/generators/combine-latest-deep-object.generator';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom, type Observable, Subject } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import type { ClassificationNodeId } from '../../../interfaces/Classification.interface';
import { BrandingProvider } from '../../../providers/branding/branding.provider';
import { DashboardingProvider } from '../../../providers/dashboarding.provider';
import { DashboardsProvider } from '../../../providers/dashboards/dashboards.provider';
import { isLegacyDashboard } from '../../../providers/dashboards/dashboards.utils';
import { LegacyDashboardsProvider } from '../../../providers/dashboards/legacy-dashboards.provider';
import {
    type ReportGenerationDescription,
    ReportGenerationType,
    type ReportPlan,
    type ReportPlanId,
    type RequestPlanCreationBody,
    type RequestPlanUpdateBody
} from '../reports.interface';
import { AdvizeoCoreReportsService } from './advizeo-core-reports.service';

@Injectable({
    providedIn: 'root'
})
export class ReportsService {
    private _reportsCoreService = inject(AdvizeoCoreReportsService);
    private _dashboardingProvider = inject(DashboardingProvider);
    private _legacyDashboardsProvider = inject(LegacyDashboardsProvider);
    private _brandingProvider = inject(BrandingProvider);
    private _dashboardsProvider = inject(DashboardsProvider);

    private _reportPlansUpdated$ = new Subject<void>();

    listenToReportPlansUpdate$(): Observable<void> {
        return this._reportPlansUpdated$;
    }

    generateReportOneShot(
        period: number,
        enableEstimated: boolean,
        report: ReportGenerationDescription,
        classificationNodeId: ClassificationNodeId
    ): Promise<string> {
        return firstValueFrom(
            this._reportsCoreService.generateReportOneShot$(
                period,
                enableEstimated,
                report.code,
                classificationNodeId,
                this._brandingProvider.getBranding()!.rootUri,
                report.generationType
            )
        );
    }

    createReportPlan(request: RequestPlanCreationBody): Promise<void> {
        return firstValueFrom(this._reportsCoreService.createReportPlan$(request)).then(() => {
            this._reportPlansUpdated$.next();
        });
    }

    updateReportPlan(request: RequestPlanUpdateBody): Promise<void> {
        return firstValueFrom(this._reportsCoreService.updateReportPlan$(request)).then(() => {
            this._reportPlansUpdated$.next();
        });
    }

    addReportSubscription(reportPlanId: ReportPlanId, email: string): Promise<void> {
        return firstValueFrom(this._reportsCoreService.addReportSubscription$(reportPlanId, email)).then(() => {
            this._reportPlansUpdated$.next();
        });
    }

    deleteReportSubscription(reportPlanId: ReportPlanId, email: string): Promise<void> {
        return firstValueFrom(this._reportsCoreService.deleteReportSubscription$(reportPlanId, email)).then(() => {
            this._reportPlansUpdated$.next();
        });
    }

    fetchReportsPlans(isSubscriber: boolean, isCreator: boolean, searchOnAdminScope: boolean): Promise<ReportPlan[]> {
        return firstValueFrom(this._reportsCoreService.fetchReportsPlans$(isSubscriber, isCreator, searchOnAdminScope));
    }

    deleteReportPlan(reportPlanId: ReportPlanId): Promise<void> {
        return firstValueFrom(this._reportsCoreService.deleteReportPlan$(reportPlanId));
    }

    updateSubscriptionStatus(reportPlanId: ReportPlanId, isActive: boolean): Promise<void> {
        return firstValueFrom(this._reportsCoreService.updateSubscriptionStatus$(reportPlanId, isActive)).then(() => {
            this._reportPlansUpdated$.next();
        });
    }

    updateRecipientSubscriptionStatus(reportPlanId: ReportPlanId, recipientEmail: string, isActive: Boolean): Promise<void> {
        return firstValueFrom(this._reportsCoreService.updateRecipientSubscriptionStatus$(reportPlanId, recipientEmail, isActive));
    }

    getAvailableReportTypes$(): Observable<ReportGenerationDescription[]> {
        return this._dashboardsProvider.getDashboardIds$().pipe(
            switchMap(dashboardIds => {
                return combineLatestDeepObject(
                    dashboardIds.map(dashboardId => {
                        return {
                            code: dashboardId.toString(),
                            label: this._dashboardsProvider.getDashboardName$(dashboardId),
                            generationType: isLegacyDashboard(dashboardId)
                                ? ReportGenerationType.LEGACY
                                : ReportGenerationType.TAILORED_DASHBOARD
                        };
                    })
                );
            })
        );
    }

    getCurrentlyPrintingReportType$(): Observable<string> {
        return this._dashboardingProvider.getSelectedDashboardId$().pipe(
            filter(isNotNullish),
            switchMap(dashboardId => this._dashboardsProvider.getDashboardName$(dashboardId))
        );
    }

    async isReportTypeAllowingPeriod(reportTypeCode: string): Promise<boolean> {
        const reportTypes = await firstValueFrom(this.getAvailableReportTypes$());

        const reportType = reportTypes.find(r => r.code === reportTypeCode);
        if (!reportType) return false;

        switch (reportType.generationType) {
            case ReportGenerationType.TAILORED_DASHBOARD:
                return true;
            case ReportGenerationType.LEGACY:
                const dashboard = this._legacyDashboardsProvider
                    .getLegacyDashboards()
                    .find(dashboard => dashboard.dashboardRight === reportType.code);
                return Boolean(dashboard && dashboard.showPeriodsList);
            default:
                return false;
        }
    }

    isReportTypeAvailableInMonoSiteOnly(reportType: string): boolean {
        const dashboard = this._legacyDashboardsProvider.getLegacyDashboards().find(dashboard => dashboard.dashboardRight === reportType);
        // ReportType is a dashboard
        if (dashboard) {
            return this._dashboardingProvider.isDashboardAvailableInMonoSiteOnly(reportType);
        } else {
            // To implement for other type of reports (actions, alerts etc..)
            return false;
        }
    }
}
